import { useState } from 'react';
import { api, type AdminMember, type ApiToken } from '../api';
import { useEffect } from 'react';
import { isKeyRequestCancelled, useSession } from '../session';
import { formatTime } from '../time';
import { prove } from '../keys';
import { errorText, useT } from '../i18n';
import { LaunchCard } from './AdminBilling';
import { AdminPayments } from './AdminPayments';
import { PilotUsers, PilotSettings } from './PilotUsers';

export function Admin() {
 const t=useT(); const {me,withKey,refresh}=useSession();
 const [tab,setTab]=useState<'users'|'payments'|'settings'>('users');
 const [generation,setGeneration]=useState(0); const [error,setError]=useState<string|null>(null);
 const reload=async()=>{setGeneration(g=>g+1);};
  async function resetIdentity(id: number, name: string) {
    if (!confirm(t.admin.confirmReset(name))) return;
    setError(null);
    const reason = prompt(t.admin.askReason(name))?.trim() ?? "";
    if (reason.length < 3 || reason.length > 200) {
      setError(t.admin.reasonNeeded);
      return;
    }
    try {
      // The server needs a fresh proof by the admin's own key over this member and the reason (PLAN.md 4.13 R5).
      await withKey(t.keyDialog.purposes.reset, async (key) => api.resetIdentity(id, reason, await prove(key, "admin-reset", [String(id), reason])));
      await Promise.all([refresh(), reload()]);
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(errorText(err));
    }
  }

  /** The owner makes a member an admin or an admin a member again (PLAN.md 4.22), confirmed with the owner's key. */
  async function changeRole(m: AdminMember, role: "admin" | "member") {
    if (!confirm(role === "admin" ? t.admin.confirmMakeAdmin(m.display_name, m.email) : t.admin.confirmRemoveAdmin(m.display_name))) return;
    setError(null);
    const reason = prompt(t.admin.askRoleReason(m.display_name))?.trim() ?? "";
    if (reason.length < 3 || reason.length > 200) {
      setError(t.admin.roleReasonNeeded);
      return;
    }
    try {
      await withKey(t.keyDialog.purposes.role, async (key) => api.setRole(m.id, role, reason, await prove(key, "admin-role", [String(m.id), role, reason])));
      await reload();
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(errorText(err));
    }
  }


 return <div className="admin-page">
  <div className="page-head"><h1>{t.admin.title}</h1></div>
  <div className="chips list-tabs admin-tabs">{(['users','payments','settings'] as const).map(k=><button key={k} className={tab===k?'chip on':'chip'} onClick={()=>setTab(k)}>{k==='users'?t.pilotAdmin.users:k==='payments'?t.adminPayments.tab:t.design.settingsTab}</button>)}</div>
  {error&&<p className="error" role="alert">{error}</p>}
  {tab==='users'&&<PilotUsers key={generation} onRole={changeRole} onReset={resetIdentity}/>}
  {tab==='payments'&&<AdminPayments owner={me.role==='owner'}/>}
  {tab==='settings'&&<><PilotSettings owner={me.role==='owner'}/><LaunchCard/></>}
 </div>;
}

/** A service account's API tokens for the admin: names and dates, never the token; revoke (PLAN.md 4.16 K5). */
export function ServiceTokens({ memberId }: { memberId: number }) {
  const t = useT();
  const [list, setList] = useState<ApiToken[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => api.adminTokens(memberId).then(setList, (err) => setError(errorText(err)));
  useEffect(() => { void load(); }, [memberId]);
  return (
    <div className="small">
      <p>{t.developers.tokensTitle}</p>
      {list && list.length === 0 && <p className="muted">{t.developers.noTokens}</p>}
      <ul className="rows">
        {list?.map((tk) => (
          <li key={tk.id}>
            <span className="grow">{tk.name} <code>{tk.prefix}…</code> <span className="muted">{tk.revoked_at ? t.developers.revoked : tk.last_used_at ? t.developers.lastUsed(formatTime(tk.last_used_at * 1000)) : t.developers.neverUsed}</span></span>
            {!tk.revoked_at && <button className="link small" onClick={() => void api.adminRevokeToken(tk.id).then(load, (err) => setError(errorText(err)))}>{t.developers.revoke}</button>}
          </li>
        ))}
      </ul>
      {error && <p className="error">{error}</p>}
    </div>
  );
}
